import { useCallback, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { layout, space } from "@tokens/index";
import { SkeletonRows, useViewport } from "@primitives/index";
import { staleDataStyle } from "@compositions/index";
import { AdminPage, AdminPageHeader, AdminSection, EmptyLine, ErrorAlert } from "../admin-ui";
import { useManagementGroup, useManagementGroups } from "../management-groups";
import { AdminsSection } from "./AdminsSection";
import { CreateManagementGroupForm, ManagementGroupList } from "./GroupList";
import { ManagementGroupHeader } from "./GroupHeader";
import { ScopesSection } from "./ScopesSection";
import { TeamsSection } from "./TeamsSection";

/**
 * Management groups: a master-detail page per charter §7.
 *
 * - `expanded`+: list at `layout.railWidth` beside the editor.
 * - `medium`: list at `layout.railWidthNarrow` beside the editor.
 * - `compact`: one region at a time. The list shows first; selecting a
 *   group opens the editor with a back control in its header.
 */

const PAGE_MAX_WIDTH = Math.min(1120, layout.workspaceMaxWidth);

interface ManagementGroupDetailProps {
    readonly id: string;
    readonly onBack?: () => void;
}

function ManagementGroupDetail({ id, onBack }: ManagementGroupDetailProps): ReactElement {
    const { data: group, error, refetch, isPlaceholderData } = useManagementGroup(id);

    if (error !== null) {
        return (
            <ErrorAlert onRetry={() => void refetch()}>
                Couldn&rsquo;t load group. {error.message}
            </ErrorAlert>
        );
    }
    // Skeleton only when nothing is on hand yet. A selection change shows
    // the previous group dimmed until the next one arrives.
    if (group === undefined) {
        return <SkeletonRows rows={3} rowHeight={120} shape="card" label="Loading management group" />;
    }

    return (
        // Keyed by the data's id, not the selection: the sections keep their
        // edit state while the previous group is shown, and reset the moment
        // the new group's data replaces it.
        <div
            key={group.id}
            data-testid="management-group-detail"
            style={{ display: "grid", gap: space[5], minWidth: 0, ...staleDataStyle(isPlaceholderData) }}
        >
            <ManagementGroupHeader group={group} {...(onBack !== undefined ? { onBack } : {})} />
            <AdminsSection group={group} />
            <ScopesSection group={group} />
            <TeamsSection group={group} />
        </div>
    );
}

export function ManagementGroupsAdminPage(): ReactElement {
    const { viewport, isCompact } = useViewport();
    const listQuery = useManagementGroups(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [detailOpen, setDetailOpen] = useState(false);

    // Memoised so the dependency arrays below see a stable reference; the
    // raw `data?.items ?? []` form returns a fresh array on every render.
    const items = useMemo(() => listQuery.data?.items ?? [], [listQuery.data]);

    // Auto-select the first group once the list loads so a fresh visit shows
    // detail immediately. Keeping this in a derived value (not an effect)
    // avoids a flash of "select a group" before the data arrives.
    const effectiveSelectedId = useMemo(() => {
        if (selectedId !== null && items.some((i) => i.id === selectedId)) {
            return selectedId;
        }
        return items[0]?.id ?? null;
    }, [items, selectedId]);

    const handleSelect = useCallback((id: string) => {
        setSelectedId(id);
        setDetailOpen(true);
    }, []);

    const handleBack = useCallback(() => {
        setDetailOpen(false);
    }, []);

    const refetchList = useCallback(() => void listQuery.refetch(), [listQuery]);

    const railWidth = viewport === "medium" ? layout.railWidthNarrow : layout.railWidth;
    const splitStyle: CSSProperties = {
        display: "grid",
        gridTemplateColumns: `${String(railWidth)}px minmax(0, 1fr)`,
        gap: space[5],
        alignItems: "start",
        minWidth: 0,
    };

    const list = (
        <div
            data-testid="management-group-list"
            style={{ display: "grid", gap: space[3], minWidth: 0 }}
        >
            <CreateManagementGroupForm onCreated={handleSelect} />
            <ManagementGroupList
                items={items}
                selectedId={effectiveSelectedId}
                onSelect={handleSelect}
                isLoading={listQuery.isLoading}
                error={listQuery.error}
                onRetry={refetchList}
            />
        </div>
    );

    const detail =
        effectiveSelectedId === null ? (
            <AdminSection>
                <EmptyLine>
                    Select a management group to see its details, or create one with the button
                    above.
                </EmptyLine>
            </AdminSection>
        ) : (
            <ManagementGroupDetail
                id={effectiveSelectedId}
                {...(isCompact ? { onBack: handleBack } : {})}
            />
        );

    if (isCompact) {
        return (
            <AdminPage maxWidth={PAGE_MAX_WIDTH}>
                {detailOpen && effectiveSelectedId !== null ? (
                    detail
                ) : (
                    <>
                        <AdminPageHeader
                            title="Management groups"
                            description="Organize ownership of workflows and queues. Each group has its own admins, workflow scopes, and teams."
                        />
                        {list}
                    </>
                )}
            </AdminPage>
        );
    }

    return (
        <AdminPage maxWidth={PAGE_MAX_WIDTH}>
            <AdminPageHeader
                title="Management groups"
                description="Organize ownership of workflows and queues. Each group has its own admins, workflow scopes, and teams."
            />
            <div data-testid="management-group-split" style={splitStyle}>
                {list}
                <div style={{ minWidth: 0 }}>{detail}</div>
            </div>
        </AdminPage>
    );
}
